import { observer } from 'mobx-react-lite';
import React, { FC, useEffect } from 'react';
import { ROUTES } from '~/routes/routes';
import { useLocation, useNavigate } from 'react-router-dom';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { useService } from '~/services/service.base';
import { WdollarCard } from '~/views/molecules/wdollar.card';
import { PaymentType } from '~/services/service.enums';
import { WrapperService } from '~/services/service.wrapper';
import { BackBtn } from '~/views/atoms/back.btn';
import { PaysafeForm } from '~/views/organisms/paysafe.form';

export const Payment: FC = observer(() => {
    const wrapperService = useService(WrapperService);
    const { paymentService, authService, paysafeFormService } = wrapperService;
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        if (authService.redirectToHome) {
            navigate(ROUTES.MAIN.path);
        }
    }, []);

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }, [location.pathname, paymentService.showSuccessBlock]);

    const paypalOptions = {
        clientId: paymentService.paypalClientId,
        currency: 'USD',
        intent: 'capture',
    };

    useEffect(() => {
        (async () => {
            paymentService.setShowSuccessBlock(false);
            if (!paymentService.wdollarPackages.length && authService.currentUser) {
                await paymentService.getWdollarPackages(authService);
                paymentService.getWdollarPackage(location.state.id);
            } else if (authService.currentUser) {
                paymentService.getWdollarPackage(location.state.id);
            }
        })();
    }, [authService.currentUser]);

    return (
        <section className="confirm-and-pay" id="confirm-and-pay">
            <div className="container">
                <BackBtn path={ROUTES.MAIN.path} beforeGoBack={() => paysafeFormService.clearAllErrors()} />
                <div className="confirm-and-pay-content">
                    {!paymentService.showSuccessBlock && (
                        <div className="payment-method-wrap">
                            <React.Fragment>
                                {false && (
                                    <div className="payment-switcher">
                                        <div className="payment-switcher-text">
                                            <h5>Pay with</h5>
                                        </div>
                                        <div className="payment-switcher-options">
                                            <a
                                                className={
                                                    paymentService.paymentMethod === PaymentType.paypal
                                                        ? 'payment-switcher-selected'
                                                        : ''
                                                }
                                                href="#"
                                                onClick={paymentService.handleSwitchPayment}
                                            >
                                                Paypal
                                            </a>
                                            <a
                                                className={
                                                    paymentService.paymentMethod === PaymentType.creditCard
                                                        ? 'payment-switcher-selected'
                                                        : ''
                                                }
                                                href="#"
                                                onClick={paymentService.handleSwitchPayment}
                                            >
                                                Credit Card
                                            </a>
                                        </div>
                                    </div>
                                )}
                            </React.Fragment>
                            {paymentService.paymentMethod === PaymentType.creditCard && (
                                <PaysafeForm
                                    wdollarPackage={paymentService.selectedWdollarPackage}
                                    onAfterPayment={paymentService.onAfterPayment}
                                />
                            )}
                            {paymentService.paymentMethod === PaymentType.paypal && (
                                <div className="paypal-method">
                                    <div className="paypal-method-top">
                                        <div className="paypal-method-text">Paypal</div>
                                        <div className="paypal-logo-wrap">
                                            <figure>
                                                <img src="/assets/img/paypal-logo1.svg" />
                                            </figure>
                                        </div>
                                    </div>
                                    <div className="pm-btn-wrap">
                                        <PayPalScriptProvider options={paypalOptions}>
                                            <PayPalButtons
                                                style={{ layout: 'horizontal' }}
                                                createOrder={paymentService.createOrder}
                                                onApprove={paymentService.paypalOnApprove}
                                            />
                                        </PayPalScriptProvider>
                                    </div>
                                    <div className="pm-logo-wrap">
                                        <figure>
                                            <img src="/assets/img/paypal-logo2.svg" />
                                        </figure>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    {paymentService.showSuccessBlock && (
                        <div className="payment-success-wrap">
                            <h3>Congratulations!</h3>
                            <p className="purchase-completed">Your purchase has been completed</p>
                            <p className="dollars-added">
                                {paymentService.order?.packageFreePlay > 0 ? (
                                    <span>
                                        ${paymentService.order?.packagePrice} WDollars &amp; $
                                        {paymentService.order?.packageFreePlay} Free Play have been added to your
                                        Balance.
                                    </span>
                                ) : (
                                    <span>
                                        ${paymentService.order?.packagePrice} WDollars have been added to your Balance.
                                    </span>
                                )}
                            </p>
                            <div className="total-wrap">
                                <div className="balance-title">My balance:</div>
                                <div className="balance-total">
                                    <figure>
                                        <img src="/assets/img/letter-w.svg" />
                                    </figure>
                                    <span className="price-label">Dollars</span>
                                    {authService.showBalance && (
                                        <span id="balance-total" className="price">
                                            ${authService.currentUser?.balance}
                                        </span>
                                    )}
                                </div>
                                <div className="free-play-total">
                                    <figure>
                                        <img src="/assets/img/free-play-icon.svg" />
                                    </figure>
                                    <span className="price-label">Free Play </span>
                                    {authService.showBalance && (
                                        <span className="price">${authService.currentUser?.freePlay}</span>
                                    )}
                                </div>
                            </div>
                            <h4>Details</h4>
                            <div className="order-details">
                                <div className="order-details-row">
                                    <div className="od-label">Purchase number:</div>
                                    <div className="od-value">{paymentService.order?.id}</div>
                                </div>
                                <div className="order-details-row">
                                    <div className="od-label">Date:</div>
                                    <div className="od-value">{paymentService.order?.date}</div>
                                </div>
                                <div className="order-details-row">
                                    <div className="od-label">Total:</div>
                                    <div className="od-value">${paymentService.order?.total}</div>
                                </div>
                                <div className="order-details-row">
                                    <div className="od-label">Payment method:</div>
                                    <div className="od-value">{paymentService.order?.paymentMethod}</div>
                                </div>
                            </div>
                            {false && (
                                <div className="order-notes">
                                    <div className="order-notes-label">Notes</div>
                                    <div className="order-notes-text">
                                        Please check your email for additional information
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    <div className="wallet-wrap">
                        {paymentService.showSuccessBlock && (
                            <div className="congrat-mobile">
                                <h3 id="congrat-mobile">Congratulations!</h3>
                                <p className="purchase-completed">Your purchase has been completed</p>
                            </div>
                        )}
                        <div className={'wallet-inner'}>
                            {paymentService.showSuccessBlock === false && (
                                <h4>Please take a moment to review you purchase</h4>
                            )}
                            <div className="wallet-item-wrap">
                                {paymentService.selectedWdollarPackage ? (
                                    <WdollarCard hideBuyButton={true} item={paymentService.selectedWdollarPackage} />
                                ) : null}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
});
